import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom'; // Import Link and useLocation for navigation
import SEO from '../components/SEO';
import windowCleaning from '../assets/IMG_5326.HEIC.png';
import gutterCleaning from '../assets/GutterCleaningBeforeAfter.JPG';
import polymericSanding from '../assets/polymeric-sanding-new.jpg';
import houseWashing from '../assets/house-washing-services.jpg';
import './Services.css';

function Services() {
    const location = useLocation();

    useEffect(() => {
        // Check if there's a hash in the URL
        if (location.hash) {
            // Remove the # from the hash
            const id = location.hash.substring(1);

            // Find the element with the id
            const element = document.getElementById(id);

            // If the element exists, scroll to it
            if (element) {
                // Add a slight delay to ensure the page is fully loaded
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 300);
            }
        }
    }, [location]);

    const services = [
        {
            id: 1,
            title: 'Window Cleaning',
            description: 'Professional window cleaning services for homes and businesses.',
            image: windowCleaning,
            process: 'Our comprehensive window cleaning process involves the use of eco-friendly cleaning solutions and professional-grade equipment to ensure streak-free results. We clean both the interior and exterior of your windows, including screens, tracks, and frames, leaving them sparkling clean. Our attention to detail and commitment to quality have made us Fredericton\'s preferred window cleaning service.',
            features: [
                'Residential and commercial services',
                'Interior and exterior window cleaning',
                'Screen cleaning and frame wiping',
                'High-rise window cleaning',
                'Eco-friendly cleaning solutions',
                'Fully insured professionals'
            ],
            isPrimary: true
        },
        {
            id: 2,
            title: 'Gutter Cleaning',
            description: 'Keep your gutters clean and functional with our expert services.',
            image: gutterCleaning,
            process: 'Our gutter cleaning process includes removing debris, flushing the gutters, and inspecting for any damage. We ensure your gutters are free of clogs and functioning properly to prevent damage to your property.',
            features: [
                'Complete debris removal',
                'Downspout flushing',
                'Gutter inspection',
                'Minor repairs available',
                'Fully insured professionals'
            ]
        },
        {
            id: 3,
            title: 'Polymeric Sanding',
            description: 'Enhance the durability and appearance of your pavers with polymeric sanding.',
            image: polymericSanding,
            process: 'Our polymeric sanding process involves removing all weeds and existing sand from the joints, folllowed by filling the joints between pavers with high-quality polymeric sand, which hardens when wet. This stabilizes the pavers, and enhances their longevity as well as look.',
            features: [
                'Weed removal',
                'Paver stabilization',
                'Enhanced durability',
                'Improved appearance',
                'Fully insured professionals'
            ]
        },
        {
            id: 4,
            title: 'House Washing',
            description: 'Refresh your siding and exterior surfaces with a safe, thorough house wash.',
            image: houseWashing,
            process: 'Our house washing service removes dirt, algae, mildew, and surface buildup from your home\'s exterior using the right cleaning methods for the material being treated. We focus on delivering a noticeably cleaner finish while helping protect your siding and improve curb appeal.',
            features: [
                'Vinyl and exterior surface cleaning',
                'Removal of dirt, algae, and mildew',
                'Improved curb appeal',
                'Attention to delicate exterior finishes',
                'Fully insured professionals'
            ]
        },
        {
            id: 5,
            title: 'Pressure Washing',
            description: 'Restore hard outdoor surfaces with professional pressure washing.',
            process: 'Our pressure washing service is ideal for hard surfaces that collect stubborn grime, organic buildup, and staining over time. We clean areas such as driveways, walkways, patios, and other durable surfaces to leave them looking brighter, safer, and better maintained.',
            features: [
                'Driveways and walkways',
                'Patios and hardscape surfaces',
                'Removal of stubborn buildup',
                'Cleaner, brighter exterior surfaces',
                'Fully insured professionals'
            ],
            hasImage: false
        },
    ];

    return (
        <div className="services-page">
            <SEO
                title="Window Cleaning, Gutter Cleaning, House Washing & Pressure Washing Fredericton"
                description="Explore Prestige Clean services in Fredericton, NB including window cleaning, gutter cleaning, house washing, pressure washing, and polymeric sanding for homes and commercial properties."
                keywords="window cleaning services fredericton, gutter cleaning services fredericton, house washing fredericton, pressure washing fredericton, polymeric sanding fredericton, exterior cleaning fredericton, commercial window cleaning fredericton"
                canonicalUrl="https://prestigecleanhomeservices.ca/services"
                type="service"
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://prestigecleanhomeservices.ca/" },
                            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://prestigecleanhomeservices.ca/services" }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "name": "Prestige Clean Services",
                        "itemListElement": [
                            { "@type": "Service", "position": 1, "name": "Window Cleaning Fredericton" },
                            { "@type": "Service", "position": 2, "name": "Gutter Cleaning Fredericton" },
                            { "@type": "Service", "position": 3, "name": "Polymeric Sanding Fredericton" },
                            { "@type": "Service", "position": 4, "name": "House Washing Fredericton" },
                            { "@type": "Service", "position": 5, "name": "Pressure Washing Fredericton" }
                        ]
                    }
                ]}
            />
            <div className="services-banner">
                <span className="services-banner__eyebrow">Residential and commercial</span>
                <h1>Our Professional Services</h1>
                <p>Professional exterior cleaning services to keep your property looking its best</p>
            </div>

            <div className="services-detail">
                {services.map((service) => (
                    <div
                        key={service.id}
                        id={service.title.toLowerCase().replace(' ', '-')}
                        className={`service-detail-item ${service.isPrimary ? 'primary-service' : ''} ${service.hasImage === false ? 'no-service-image' : ''}`}
                    >
                        {service.hasImage !== false && (
                            <div className="service-detail-image">
                                <img src={service.image} alt={service.title} />
                            </div>
                        )}
                        <div className="service-detail-content">
                            <h2>{service.title}</h2>
                            <p className="service-description">{service.description}</p>
                            <h3>Our Process</h3>
                            <p>{service.process}</p>
                            <h3>Service Features</h3>
                            <ul className="service-features">
                                {service.features.map((feature, index) => (
                                    <li key={index}>{feature}</li>
                                ))}
                            </ul>
                            <Link to="/contact#contact-form" className="cta-button">Request a Quote</Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Services;
